import React, { useEffect, useRef, useState } from 'react';
import { useNode } from '@craftjs/core';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { PROP_POSICION, normalizarPosicion } from '../utils/posicionLibre';

/**
 * El alto del carrusel cuando el dealer lo fijó con los cuadritos.
 *
 * El carrusel se declaraba `height: auto` dentro de un lienzo, así que se medía
 * por su espaciador —la primera diapositiva— e ignoraba el alto que le daba su
 * envoltorio. En el editor eso hacía que el rectángulo de selección y lo que se
 * ve dejaran de ser la misma geometría; acá, que el alto elegido no se
 * publicara.
 *
 * El contrato:
 *
 * - **Sin alto fijado**: nada cambia. El carrusel se mide por su contenido, que
 *   es lo que evita el colapso a cero del ticket #69.
 * - **Con alto fijado**: llena exactamente su envoltorio (`height: 100%`) y la
 *   foto se encuadra con `cover` — recorta, no deforma.
 *
 * `h` y `m.h` se aplican cada uno de su lado del corte y no se mezclan.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
const CLASES_DE_ALTO = {
  ambos: 'h-full',
  soloEscritorio: 'lg:h-full max-lg:h-auto',
  soloMovil: 'max-lg:h-full lg:h-auto',
} as const;

const clasesDeAlto = (enLienzo: boolean, alto: boolean, altoMovil: boolean): string | undefined => {
  if (!enLienzo || (!alto && !altoMovil)) return undefined;
  if (alto && altoMovil) return CLASES_DE_ALTO.ambos;
  return alto ? CLASES_DE_ALTO.soloEscritorio : CLASES_DE_ALTO.soloMovil;
};

/**
 * Si el alto va en el `style` o lo ponen las clases. En línea sólo cuando no
 * hay ningún alto fijado: con alto fijado tiene que mandar la clase, porque un
 * estilo en línea le ganaría y volvería a ignorar el envoltorio.
 */
const altoEnLinea = (enLienzo: boolean, alto: boolean, altoMovil: boolean): boolean =>
  !enLienzo || (!alto && !altoMovil);

export interface DiapositivaCarousel {
  src?: string;
  alt?: string;
}

interface CarouselProps {
  slides?: DiapositivaCarousel[];
  autoplay?: boolean;
  /** Cada cuántos segundos pasa sola. */
  intervalo?: number;
  flechas?: boolean;
  indicadores?: boolean;
  objectFit?: 'cover' | 'contain';
  borderRadius?: number;
  /** Alto cuando NO está en un lienzo; ahí el frame lo da el envoltorio. */
  height?: number;
  /** Sacado de la página publicada sin borrarlo. En el editor sigue, tenue. */
  oculto?: boolean;
}

export const DIAPOSITIVAS_POR_DEFECTO: DiapositivaCarousel[] = [
  { src: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&h=800&fit=crop', alt: 'Deportivo de lujo' },
  { src: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=1200&h=800&fit=crop', alt: 'Elegancia en movimiento' },
  { src: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=1200&h=800&fit=crop', alt: 'Potencia y diseño' },
];

/** Las props del panel pueden llegar como texto. */
const aBool = (v: unknown, porDefecto: boolean): boolean =>
  v === true || v === 'true' ? true : v === false || v === 'false' ? false : porDefecto;
const aNum = (v: unknown, porDefecto: number): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : porDefecto;
};

/**
 * Carrusel de imágenes.
 *
 * Es un COMPONENTE del Toolbox, no una sección: vive adentro de una `Section`,
 * se mueve y se redimensiona con el sistema libre como cualquier otro elemento,
 * y el frame lo da su envoltorio. La sección `ImageCarousel` que ya existía es
 * otra cosa —una cinta continua a lo ancho de la página— y se queda como está.
 *
 * Las diapositivas se apilan en el mismo lugar y se cruzan por opacidad: así el
 * alto no depende de la imagen que esté visible y el frame nunca salta.
 *
 * ## Los controles escuchan en el DOM y no con `onClick`
 *
 * Dentro del canvas del editor el `onClick` de React no llega a estos botones
 * —se comprobó: el `click` nativo sí les llega, el handler de React no corre— y
 * las flechas quedaban muertas justo donde el dealer las prueba. Un listener
 * nativo delegado en el contenedor anda igual en los dos lados y no depende de
 * cómo esté montado el árbol.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/Carousel.tsx.
 * Las diferencias son las del editor: allá el autoplay se frena mientras el
 * elemento está seleccionado —para no pisarle los estilos al arrastre— y se
 * dibuja el botón de borrar. Acá anda siempre.
 */
export const Carousel = ({
  slides = DIAPOSITIVAS_POR_DEFECTO,
  autoplay = true,
  intervalo = 4,
  flechas = true,
  indicadores = true,
  objectFit = 'cover',
  borderRadius = 8,
  height = 320,
  oculto = false,
}: CarouselProps) => {
  const cajaRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const { connectors, enLienzo, conAltoPropio, conAltoPropioMovil } = useNode((state) => {
    const pos = normalizarPosicion((state.data.props as Record<string, unknown>)?.[PROP_POSICION]);
    return {
      enLienzo: !!pos,
      // El alto que el dealer fijó con los cuadritos, de cada lado del corte.
      conAltoPropio: !!pos?.h,
      conAltoPropioMovil: !!pos?.m?.h,
    };
  });

  const validas = (slides ?? []).filter((s) => s && s.src);
  const [actual, setActual] = useState(0);
  const total = validas.length;

  // Si se borran diapositivas desde el panel, el índice puede quedar afuera.
  useEffect(() => {
    if (actual >= total) setActual(0);
  }, [actual, total]);

  const anda = aBool(autoplay, true) && total > 1;
  const segundos = Math.max(1, aNum(intervalo, 4));

  useEffect(() => {
    if (!anda) return;
    const t = setInterval(() => setActual((i) => (i + 1) % total), segundos * 1000);
    return () => clearInterval(t);
  }, [anda, segundos, total]);

  // El total viaja por ref: el listener se engancha una sola vez y siempre lee
  // el valor de ahora, sin tener que re-engancharse cuando cambian las imágenes.
  const totalRef = useRef(total);
  totalRef.current = total;

  /** Un solo listener delegado para las flechas y los puntitos. */
  const alClic = useRef((e: Event) => {
    const t = totalRef.current;
    if (t < 2) return;
    const boton = (e.target as HTMLElement | null)?.closest?.('[data-carousel-ir]') as HTMLElement | null;
    if (!boton) return;
    e.stopPropagation();
    const a = boton.dataset.carouselIr ?? '';
    setActual((i) => {
      if (a === 'anterior') return (i - 1 + t) % t;
      if (a === 'siguiente') return (i + 1) % t;
      const n = Number(a);
      return Number.isFinite(n) ? Math.max(0, Math.min(t - 1, n)) : i;
    });
  });

  /**
   * El listener se engancha en el ref y no en un efecto.
   *
   * React vuelve a llamar al ref en cada render y el elemento puede cambiar; un
   * efecto con dependencias se quedaría escuchando en el nodo viejo —que ya no
   * está en la página— y las flechas dejarían de responder sin aviso.
   */
  const ponerCaja = (el: HTMLDivElement | null) => {
    if (cajaRef.current && cajaRef.current !== el) {
      cajaRef.current.removeEventListener('click', alClic.current);
    }
    cajaRef.current = el;
    if (!el) return;
    // `addEventListener` con la misma función es idempotente.
    el.addEventListener('click', alClic.current);
    connectors.connect(el);
  };

  // Ocultarlo es no dibujarlo; en el editor sigue estando, tenue.
  if (oculto) return null;

  const radio = aNum(borderRadius, 8);
  const control: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(17,24,39,.55)',
    color: '#ffffff',
    fontSize: 18,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  };

  return (
    <div
      ref={ponerCaja}
      data-carousel=""
      data-slide-visible={actual}
      className={clasesDeAlto(enLienzo, conAltoPropio, conAltoPropioMovil)}
      style={{
        position: 'relative',
        width: '100%',
        // Con diapositivas y dentro de un lienzo el alto lo pone el espaciador
        // de abajo: la caja es la foto, como en el sitio de referencia. Sin
        // diapositivas vuelve el alto del panel — un carrusel vacío no tiene con
        // qué medirse y colapsaría a cero (ticket #69).
        ...(altoEnLinea(enLienzo, conAltoPropio, conAltoPropioMovil)
          ? { height: enLienzo ? (total > 0 ? 'auto' : `${aNum(height, 320)}px`) : `${aNum(height, 320)}px` }
          : {}),
        overflow: 'hidden',
        borderRadius: `${radio}px`,
        background: '#f3f4f6',
      }}
    >
      {/*
        El espaciador: la PRIMERA diapositiva, en el flujo y sin pintar, para que
        la caja tome su proporción real. Las demás se apilan encima en absoluto,
        así que el alto no cambia al pasar de una a otra y el frame nunca salta.

        Va sin JS a propósito: medir con `onLoad` deja un salto de layout en la
        primera pintada y no funciona en el render del servidor.

        `aria-hidden` y `alt` vacío: es geometría, no contenido — la diapositiva
        de verdad ya está abajo con su texto alternativo.
      */}
      {enLienzo && total > 0 && (
        <img
          src={validas[0].src}
          alt=""
          aria-hidden="true"
          data-carousel-espaciador=""
          style={{ display: 'block', width: '100%', height: 'auto', visibility: 'hidden' }}
          draggable={false}
        />
      )}

      {total === 0 ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: 13,
            textAlign: 'center',
            padding: 12,
          }}
        >
          {t('site.carousel.empty')}
        </div>
      ) : (
        validas.map((s, i) => (
          <img
            key={`${s.src}-${i}`}
            src={s.src}
            alt={s.alt || `${t('site.carousel.image')} ${i + 1}`}
            data-slide={i}
            // Abajo de `xl` el recorte se apaga.
            //
            // El carrusel no puede medirse solo —sus diapositivas son absolutas,
            // así que la caja se la da el envoltorio— y esa caja conserva la
            // proporción con la que se diseñó, que no es la de la foto. Con
            // `cover` la diferencia se come los bordes: medido en el banner de B2
            // Autos, caja 1.76 contra foto 1.96, el 10% del ancho — y ahí es donde
            // suelen ir el texto y el precio. Arriba del corte manda lo que el
            // dealer eligió en el panel.
            //
            // En clase y no en el `style`: un estilo en línea le gana a la media
            // query.
            className={
              enLienzo && objectFit !== 'contain'
                ? (conAltoPropio && conAltoPropioMovil
                  ? 'object-cover'
                  : (conAltoPropio
                    ? 'object-contain lg:object-cover'
                    : (conAltoPropioMovil
                      ? 'max-lg:object-cover object-contain xl:object-cover'
                      : 'object-contain xl:object-cover')))
                : undefined
            }
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              ...(enLienzo && objectFit !== 'contain'
                ? null
                : { objectFit: objectFit === 'contain' ? 'contain' : 'cover' }),
              opacity: i === actual ? 1 : 0,
              transition: 'opacity .45s ease',
              // Solo la visible recibe el mouse: si no, arrastrar el carrusel
              // agarraría la imagen de arriba del todo, que ni se ve.
              pointerEvents: i === actual ? 'auto' : 'none',
            }}
            draggable={false}
          />
        ))
      )}

      {aBool(flechas, true) && total > 1 && (
        <>
          <button
            type="button"
            data-carousel-flecha="anterior"
            data-carousel-ir="anterior"
            aria-label={t('site.carousel.previous')}
            style={{ ...control, left: 8 }}
          >
            ‹
          </button>
          <button
            type="button"
            data-carousel-flecha="siguiente"
            data-carousel-ir="siguiente"
            aria-label={t('site.carousel.next')}
            style={{ ...control, right: 8 }}
          >
            ›
          </button>
        </>
      )}

      {aBool(indicadores, true) && total > 1 && (
        <div
          data-carousel-indicadores=""
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 8,
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {validas.map((s, i) => (
            <button
              key={`punto-${s.src}-${i}`}
              type="button"
              aria-label={`${t('site.carousel.goTo')} ${i + 1}`}
              data-carousel-ir={i}
              style={{
                width: i === actual ? 18 : 8,
                height: 8,
                borderRadius: 4,
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                background: i === actual ? '#ffffff' : 'rgba(255,255,255,.55)',
                boxShadow: '0 1px 3px rgba(0,0,0,.35)',
                transition: 'width .25s ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Carousel.craft = {
  displayName: 'Carousel',
  props: {
    slides: DIAPOSITIVAS_POR_DEFECTO,
    autoplay: true,
    intervalo: 4,
    flechas: true,
    indicadores: true,
    objectFit: 'cover',
    borderRadius: 8,
    height: 320,
  },
  // Sin `rules`, como el resto de los componentes sueltos: declarar
  // `canMoveIn: false` hacía que craft lo tratara distinto al soltarlo y el
  // manejador del lienzo no llegaba a tomar el control —el elemento entraba sin
  // `_layout`, o sea en el flujo y no donde se lo soltó.
};

export default Carousel;
